/**
 * BusinessPortalHome — the /businesses landing page. Renders the
 * spec's Business Portal home: search, browse by industry, browse by
 * location, featured / new / popular sections. All data comes from
 * ``GET /api/businesses/directory`` with different sort/filter params.
 *
 * This is a FRAMEWORK page — the sections render even when empty so
 * the layout is stable; individual features (Featured / Verified /
 * Top-Rated ranks) are TBD backend enhancements.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Building2, MapPin, Sparkles, Plus, Store, Briefcase, Package2 } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import { INDUSTRIES, UI_INDUSTRIES, labelFor } from "@/data/industries";

function BusinessCard({ biz }) {
  return (
    <Link
      to={`/business/${biz.slug}`}
      className="wofy-card !p-4 flex gap-3 hover:shadow-md transition-shadow"
      data-testid={`business-card-${biz.slug}`}
    >
      <div
        className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0 text-white font-display text-xl"
        style={{ background: biz.logo_url ? undefined : "var(--wofy-primary)" }}
      >
        {biz.logo_url
          ? <img src={biz.logo_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
          : (biz.name || "?")[0].toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="font-display text-lg truncate">{biz.name}</div>
          {biz._plan?.ecommerce && (
            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full border" style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" }}>
              Shop
            </span>
          )}
        </div>
        {biz.industry && (
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mt-0.5">
            {biz.industry}
          </div>
        )}
        {(biz.city || biz.country) && (
          <div className="text-xs opacity-70 mt-1 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {[biz.city, biz.country].filter(Boolean).join(", ")}
          </div>
        )}
      </div>
    </Link>
  );
}

function Section({ title, action, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-2xl">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function BusinessPortalHome() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [newest, setNewest] = useState([]);
  const [popular, setPopular] = useState([]);

  const search = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (industry) params.set("industry", industry);
    window.location.href = `/businesses/search?${params.toString()}`;
  };

  useEffect(() => {
    http.get("/businesses/directory", { params: { sort: "new", limit: 12 } })
      .then(({ data }) => setNewest(data?.items || []))
      .catch(() => setNewest([]));
    http.get("/businesses/directory", { params: { sort: "popular", limit: 12 } })
      .then(({ data }) => setPopular(data?.items || []))
      .catch(() => setPopular([]));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="business-portal-home">
      {/* Hero */}
      <section
        className="rounded-2xl border p-8 md:p-12 mb-10"
        style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, rgba(178,93,56,0.06), rgba(178,93,56,0.02))" }}
      >
        <div className="max-w-3xl">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">
            <Building2 className="w-3 h-3 inline-block mr-1" /> Business Portal
          </div>
          <h1 className="font-display text-4xl md:text-5xl mb-3">Discover businesses in your community.</h1>
          <p className="opacity-80 mb-6">
            Restaurants, hospitals, schools, family businesses, artisans, IT companies — every business
            gets its own website, shopfront, reviews, jobs and events, connected to the whole Social Pulse network.
          </p>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 rounded-full border px-4 py-2 bg-white" style={{ borderColor: "var(--wofy-border)" }}>
              <Search className="w-4 h-4 opacity-60" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="Search 'Acme Bakery in Kochi' or 'lawyer'"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="business-portal-search"
              />
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="text-sm bg-transparent outline-none"
                data-testid="business-portal-industry"
              >
                <option value="">All industries</option>
                {UI_INDUSTRIES.map((i) => <option key={i} value={i}>{labelFor(i)}</option>)}
              </select>
            </div>
            <button onClick={search} className="wofy-btn-primary" data-testid="business-portal-search-btn">
              Search
            </button>
            {user && (
              <Link to="/businesses/new" className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="business-portal-add">
                <Plus className="w-4 h-4" /> List your business
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Browse by industry */}
      <Section title="Browse by industry" testid="section-industries">
        <div className="flex flex-wrap gap-2">
          {UI_INDUSTRIES.map((i) => (
            <Link
              key={i}
              to={`/businesses/search?industry=${i}`}
              className="text-sm font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border hover:bg-black/5"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`industry-chip-${i}`}
            >
              {labelFor(i)}
            </Link>
          ))}
        </div>
      </Section>

      {/* New businesses */}
      <Section
        title="New on Social Pulse"
        testid="section-new"
        action={<Link to="/businesses/search?sort=new" className="text-xs font-mono uppercase tracking-wider opacity-70 hover:opacity-100">See all →</Link>}
      >
        {newest.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {newest.slice(0, 6).map((b) => <BusinessCard key={b.id} biz={b} />)}
          </div>
        ) : (
          <div className="wofy-card !p-6 text-center opacity-70 text-sm">
            No businesses yet. <Link to="/businesses/new" className="underline">Be the first</Link>.
          </div>
        )}
      </Section>

      {/* Popular */}
      <Section title="Popular" testid="section-popular"
        action={<Link to="/businesses/search?sort=popular" className="text-xs font-mono uppercase tracking-wider opacity-70 hover:opacity-100">See all →</Link>}>
        {popular.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {popular.slice(0, 6).map((b) => <BusinessCard key={b.id} biz={b} />)}
          </div>
        ) : (
          <div className="wofy-card !p-6 text-center opacity-70 text-sm">
            Ranking by followers coming soon.
          </div>
        )}
      </Section>

      {/* Quick tiles for the wider ecosystem */}
      <Section title="Do more with Social Pulse" testid="section-quicktiles">
        <div className="grid md:grid-cols-4 gap-3">
          <Link to="/marketplace" className="wofy-card !p-4 hover:shadow-md">
            <Store className="w-5 h-5 mb-1.5" style={{ color: "var(--wofy-primary)" }} />
            <div className="font-medium text-sm">Marketplace</div>
            <div className="text-xs opacity-70 mt-0.5">Buy & sell in your community</div>
          </Link>
          <Link to="/events" className="wofy-card !p-4 hover:shadow-md">
            <Sparkles className="w-5 h-5 mb-1.5" style={{ color: "var(--wofy-primary)" }} />
            <div className="font-medium text-sm">Business events</div>
            <div className="text-xs opacity-70 mt-0.5">Launches, exhibitions, training</div>
          </Link>
          <div className="wofy-card !p-4 opacity-70">
            <Briefcase className="w-5 h-5 mb-1.5" />
            <div className="font-medium text-sm">Jobs board</div>
            <div className="text-xs mt-0.5">Coming soon</div>
          </div>
          <div className="wofy-card !p-4 opacity-70">
            <Package2 className="w-5 h-5 mb-1.5" />
            <div className="font-medium text-sm">Local deals</div>
            <div className="text-xs mt-0.5">Coming soon</div>
          </div>
        </div>
      </Section>
    </div>
  );
}
